import { useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { QRScanner } from '../components/QRScanner'
import { useCart } from '../hooks/useCart'
import { playScanSound } from '../services/sounds'
import { hapticLight } from '../utils/haptic'

const isSecureContext = typeof window !== 'undefined' && window.isSecureContext

export function ScannerView() {
  const { addByQr } = useCart()
  const [active, setActive] = useState(true)
  const [lastScanned, setLastScanned] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [cameraBlocked, setCameraBlocked] = useState(false)
  const [showSuccessFlash, setShowSuccessFlash] = useState(false)

  const handleScan = useCallback(
    async (value: string) => {
      setError(null)
      const ok = await addByQr(value)
      if (ok) {
        setLastScanned(value)
        playScanSound()
        hapticLight()
        setShowSuccessFlash(true)
        setTimeout(() => setShowSuccessFlash(false), 600)
        setActive(false)
        setTimeout(() => setActive(true), 400)
      } else {
        setError('Item not found. Try another code.')
      }
    },
    [addByQr]
  )

  const handleScannerError = useCallback((msg: string) => {
    const isPermissionOrSecurity =
      /permission|secure|https|not allowed|notsupported|not supported/i.test(msg)
    if (isPermissionOrSecurity) {
      setCameraBlocked(true)
    } else {
      setError(msg)
    }
  }, [])

  if (!isSecureContext || cameraBlocked) {
    return (
      <div className="page page--scanner">
        <h1 className="page-title">Scan Grocery</h1>
        <div className="scanner-unavailable">
          <p className="scanner-unavailable-emoji" aria-hidden>📷</p>
          <h2 className="scanner-unavailable-title">Camera not available here</h2>
          <p className="scanner-unavailable-p">
            Scanning only works on a secure connection (HTTPS or when you open the app on the same computer).
            On this device you can still add items using <strong>Quick add</strong> or <strong>Categories</strong> on the home screen.
          </p>
          <Link to="/grocery" className="btn-large btn-primary" style={{ textDecoration: 'none' }}>
            Back to Home
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="page page--scanner">
      {showSuccessFlash && <div className="scanner-success-flash" aria-hidden />}
      <h1 className="page-title">Scan Grocery</h1>
      <p className="scanner-hint">Point your camera at the QR code on the item</p>
      {error && (
        <p className="scanner-error" role="alert">Item not found. Try another code.</p>
      )}
      {lastScanned && (
        <p className="scanner-added animate-pop" role="status">
          ✓ Added to cart!
        </p>
      )}
      <QRScanner onScan={handleScan} onError={handleScannerError} active={active} />
      <div className="scanner-actions">
        <Link to="/cart" className="btn-large btn-primary" style={{ textDecoration: 'none' }}>
          View Cart
        </Link>
        <Link to="/grocery" className="btn-large btn-secondary" style={{ textDecoration: 'none' }}>
          Home
        </Link>
      </div>
    </div>
  )
}
